'use client';

import * as React from 'react';

import { cn } from '@/lib/utils';

interface CardProps extends React.ComponentPropsWithRef<'div'> {
  /**
   * Card variant:
   * - "default": Solid surface with subtle elevation
   * - "data": Flat solid surface for tables and data-heavy areas
   */
  variant?: 'default' | 'data';
}

function Card({ className, variant = 'default', ref, ...props }: CardProps) {
  return (
    <div
      ref={ref}
      className={cn(
        'bg-card text-card-foreground border-border/80 relative max-w-full min-w-0 rounded-xl border',
        variant === 'default' && 'shadow-xs',
        variant === 'data' && 'shadow-none',
        className
      )}
      {...props}
    />
  );
}
Card.displayName = 'Card';

function CardHeader({
  className,
  ref,
  ...props
}: React.ComponentPropsWithRef<'div'>) {
  return (
    <div
      ref={ref}
      className={cn('flex min-w-0 flex-col space-y-1.5 p-5 sm:p-6', className)}
      {...props}
    />
  );
}
CardHeader.displayName = 'CardHeader';

function CardTitle({
  className,
  ref,
  ...props
}: React.ComponentPropsWithRef<'div'>) {
  return (
    <div
      ref={ref}
      className={cn(
        'min-w-0 text-base leading-6 font-semibold tracking-tight',
        className
      )}
      {...props}
    />
  );
}
CardTitle.displayName = 'CardTitle';

function CardDescription({
  className,
  ref,
  ...props
}: React.ComponentPropsWithRef<'div'>) {
  return (
    <div
      ref={ref}
      className={cn('text-text-3 min-w-0 text-sm leading-5', className)}
      {...props}
    />
  );
}
CardDescription.displayName = 'CardDescription';

function CardContent({
  className,
  ref,
  ...props
}: React.ComponentPropsWithRef<'div'>) {
  return (
    <div
      ref={ref}
      className={cn('min-w-0 p-5 pt-0 sm:p-6 sm:pt-0', className)}
      {...props}
    />
  );
}
CardContent.displayName = 'CardContent';

function CardFooter({
  className,
  ref,
  ...props
}: React.ComponentPropsWithRef<'div'>) {
  return (
    <div
      ref={ref}
      className={cn(
        'flex min-w-0 items-center p-5 pt-0 sm:p-6 sm:pt-0',
        className
      )}
      {...props}
    />
  );
}
CardFooter.displayName = 'CardFooter';

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
};
